import { useMemo, useState } from 'react';
import { Check, ChevronDown, Cog, Link2, Plus, User, Users } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import type { TeamMembership } from '../../../lib/api';
import type { ActiveWorkspace } from '../store/teams';

interface WorkspaceSelectorProps {
  activeWorkspace: ActiveWorkspace;
  teams: TeamMembership[];
  onWorkspaceChange: (value: string) => void;
  onCreateTeam?: () => void;
  onJoinTeam?: () => void;
  onManageTeam?: (team: TeamMembership) => void;
}

export default function WorkspaceSelector({
  activeWorkspace,
  teams,
  onWorkspaceChange,
  onCreateTeam,
  onJoinTeam,
  onManageTeam,
}: Readonly<WorkspaceSelectorProps>): JSX.Element {
  const [open, setOpen] = useState(false);

  const activeWorkspaceName = useMemo(
    () => (activeWorkspace.type === 'team' ? activeWorkspace.teamName : 'Personal'),
    [activeWorkspace],
  );

  const handleSelect = (value: string) => {
    setOpen(false);
    onWorkspaceChange(value);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between"
          aria-label="Select workspace"
        >
          <span className="truncate">{activeWorkspaceName}</span>
          <ChevronDown className="h-4 w-4 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
        <DropdownMenuItem onSelect={() => handleSelect('personal')}>
          <User className="h-4 w-4" />
          <span className="flex-1">Personal</span>
          {activeWorkspace.type === 'personal' ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : null}
        </DropdownMenuItem>

        {teams.length > 0 ? <DropdownMenuSeparator /> : null}

        {teams.map((team) => {
          const isActive =
            activeWorkspace.type === 'team' && activeWorkspace.teamId === team.teamId;

          return (
            <DropdownMenuItem
              key={team.teamId}
              className="pr-1"
              onSelect={() => handleSelect(`team:${team.teamId}`)}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <Users className="h-4 w-4 shrink-0" />
                <span className="truncate">{team.teamName}</span>
              </div>
              {isActive ? <Check className="h-4 w-4 text-green-600" /> : null}
              {onManageTeam ? (
                <button
                  type="button"
                  aria-label={`Manage ${team.teamName}`}
                  className="rounded-sm p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(false);
                    onManageTeam(team);
                  }}
                >
                  <Cog className="h-4 w-4" />
                </button>
              ) : null}
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={() => {
            setOpen(false);
            onCreateTeam?.();
          }}
        >
          <Plus className="h-4 w-4" />
          <span>Create Team</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            setOpen(false);
            onJoinTeam?.();
          }}
        >
          <Link2 className="h-4 w-4" />
          <span>Join Team</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
